import React, { useState } from 'react';
import { 
  Eye, EyeOff, Lock, Mail, User, Shield, AlertCircle, CheckCircle
} from 'lucide-react';
import Button from '../Common/Button';
import { useGlobalState } from '../../utils/globalState';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'admin'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [selectedTestAccount, setSelectedTestAccount] = useState(null);
  const { login } = useGlobalState();

  const testAccounts = [
    {
      name: 'Super Admin',
      email: 'admin@postrics.com',
      password: 'admin123',
      role: 'super_admin',
      description: 'Full access to all features and settings'
    },
    {
      name: 'Admin',
      email: 'manager@postrics.com',
      password: 'user123',
      role: 'admin',
      description: 'Manage employees, products, and appointments'
    },
    {
      name: 'User 1',
      email: 'user1@postrics.com',
      password: 'user123',
      role: 'user',
      description: 'Basic user access for testing'
    },
    {
      name: 'User 2',
      email: 'user2@postrics.com',
      password: 'user123',
      role: 'user',
      description: 'Basic user access for testing'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const result = login(formData.email, formData.password, formData.role);
      if (result.success) {
        // Login successful - the global state will handle the user
        // No need to call onLogin since we're using global state
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestAccountSelect = (account) => {
    setSelectedTestAccount(account);
    setFormData({
      email: account.email,
      password: account.password,
      role: account.role
    });
    setError('');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'employee':
        return 'bg-green-100 text-green-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <Shield className="w-4 h-4" />;
      case 'admin':
        return <User className="w-4 h-4" />;
      case 'employee':
        return <User className="w-4 h-4" />;
      case 'user':
        return <User className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  return (
         <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4 font-poppins">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
                     <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
             <span className="text-white text-2xl font-bold">Ps</span>
           </div>
          <h2 className="text-3xl font-bold text-gray-900">Postrics Salon</h2>
          <p className="mt-2 text-gray-600 font-medium">Sign in to your salon management account</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Login as</label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="employee">Employee</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                                     className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                                     className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Login Button */}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            {/* Test Accounts Toggle */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowTestAccounts(!showTestAccounts)}
                                 className="text-sm text-green-600 hover:text-green-700 font-medium"
              >
                {showTestAccounts ? 'Hide' : 'Show'} Test Accounts
              </button>
            </div>
          </form>

          {/* Test Accounts Section */}
          {showTestAccounts && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Test Accounts</h3>
              <div className="space-y-2">
                {testAccounts.map((account, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                             selectedTestAccount?.email === account.email
                         ? 'border-green-300 bg-green-50'
                         : 'border-gray-200 hover:border-green-200 hover:bg-green-25'
                    }`}
                    onClick={() => handleTestAccountSelect(account)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-1 rounded ${getRoleColor(account.role)}`}>
                          {getRoleIcon(account.role)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{account.name}</p>
                          <p className="text-xs text-gray-500">{account.email}</p>
                        </div>
                      </div>
                                             {selectedTestAccount?.email === account.email && (
                         <CheckCircle className="w-4 h-4 text-green-600" />
                       )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{account.description}</p>
                  </div>
                ))}
              </div>
              
              {selectedTestAccount && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">
                      Selected: {selectedTestAccount.name} ({selectedTestAccount.email})
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            © 2025 Postrics Salon Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;